import { isEmpty, omit, isNull } from 'lodash';
import isNil from 'lodash/isNil';
import isUndefined from 'lodash/isUndefined';
import { ISavedFilter } from 'src/components/sidebar-filters/saved-filter/interface';
import { ISidebarFilterOptionsContext } from 'src/pages/energyUsageData/sidebar/context/SidebarFilterOptionsContext';
import { CmriOverviewFilterKeys, TCmriOverviewFilterKey } from './CmriOverviewSidebarFilters';

export const transformCmriOverviewSavedFilterToUser = (
    filter: ISavedFilter['filter_values'],
    context: ISidebarFilterOptionsContext
): Record<TCmriOverviewFilterKey, string> => {
    let result: Record<TCmriOverviewFilterKey, string> = {} as Record<TCmriOverviewFilterKey, string>;

    for (const [key, value] of Object.entries(filter)) {
        // Filter out deselected values to avoid displaying them as "unknown"
        if (isUndefined(value) || isNull(value)) continue;

        switch (key) {
            case CmriOverviewFilterKeys.source:
                result[key] = value
                    .map((val: string) => {
                        const option = context.marketAwardSourceOptions.find(option => option.value === val);

                        return option?.label || 'unknown';
                    })
                    .join(', ');
                break;
            case CmriOverviewFilterKeys.status:
                result[key] = value
                    .map((val: string) => {
                        const option = context.marketAwardStatusOptions.find(option => option.value === val);

                        return option?.label || 'unknown';
                    })
                    .join(', ');
                break;
            case CmriOverviewFilterKeys.hoursRange:
                const startH = Number(value?.[0]);
                const endH = Number(value?.[1]);

                const startHisValid = startH >= 1 && startH <= 24;
                const endHisValid = endH >= 1 && endH <= 24 && endH > startH;

                if (startHisValid && endHisValid) {
                    result[key] = `[${startH}, ${endH}]`;
                } else {
                    result[key] = 'unknown';
                }

                break;
            case CmriOverviewFilterKeys.mw:
                const startMw = Number(value?.[0]);
                const endMw = Number(value?.[1]);

                const startMwIsValid = startMw >= 0 && startMw <= 10;
                const endMwIsValid = endMw >= 0 && endMw <= 10 && endMw > startMw;

                if (startMwIsValid && endMwIsValid) {
                    result[key] = `[${startMw}, ${endMw}]`;
                } else {
                    result[key] = 'unknown';
                }

                break;
            case CmriOverviewFilterKeys.price:
                const startPrice = Number(value?.[0]);
                const endPrice = Number(value?.[1]);

                const startPriceIsValid = startPrice >= 0 && startPrice <= 1000;
                const endPriceIsValid = endPrice >= 0 && endPrice <= 1000 && endPrice > startPrice;

                if (startPriceIsValid && endPriceIsValid) {
                    result[key] = `[${startPrice}, ${endPrice}]`;
                } else {
                    result[key] = 'unknown';
                }

                break;
            case CmriOverviewFilterKeys.earnings:
                const startEarning = Number(value?.[0]);
                const endEarning = Number(value?.[1]);

                const startEarningIsValid = startEarning >= 0 && startEarning <= 10000;
                const endEarningIsValid = endEarning >= 0 && endEarning <= 10000 && endEarning > startEarning;

                if (startEarningIsValid && endEarningIsValid) {
                    result[key] = `[${startEarning}, ${endEarning}]`;
                } else {
                    result[key] = 'unknown';
                }

                break;
            default:
                // @ts-ignore
                result[key] = String(value);
        }
    }

    // Remove empty properties from the result object
    const emptyProperties = getEmptyFilterProperties(result);
    if (emptyProperties.length > 0) {
        result = omit(result, emptyProperties) as Record<TCmriOverviewFilterKey, string>;
    }

    return result;
};

export const transformCmriOverviewSavedFilterValuesFromDto = (filter: ISavedFilter['filter_values']): Record<TCmriOverviewFilterKey, unknown> => {
    const result: Record<TCmriOverviewFilterKey, unknown> = {} as Record<TCmriOverviewFilterKey, unknown>;

    // Convert values from DTO format to interface format
    for (const [key, value] of Object.entries(filter)) {
        if (isUndefined(value)) continue;

        switch (key) {
            case CmriOverviewFilterKeys.resource:
            case CmriOverviewFilterKeys.source:
            case CmriOverviewFilterKeys.status:
            case CmriOverviewFilterKeys.hoursRange:
            case CmriOverviewFilterKeys.mw:
            case CmriOverviewFilterKeys.price:
            case CmriOverviewFilterKeys.earnings:
                result[key] = value;
                break;
            default:
                // @ts-ignore
                result[key] = value; // Keep other values as is
        }
    }

    return result;
};

/**
 * Transform filter into DTO for backend
 */
export const transformCmriOverviewSavedFilterValuesToDto = (filter: ISavedFilter['filter_values']): Record<TCmriOverviewFilterKey, unknown> => {
    const result: Record<TCmriOverviewFilterKey, unknown> = {} as Record<TCmriOverviewFilterKey, unknown>;

    for (const [key, value] of Object.entries(filter)) {
        switch (key) {
            case CmriOverviewFilterKeys.resource:
            case CmriOverviewFilterKeys.source:
            case CmriOverviewFilterKeys.status:
            case CmriOverviewFilterKeys.hoursRange:
            case CmriOverviewFilterKeys.mw:
            case CmriOverviewFilterKeys.price:
            case CmriOverviewFilterKeys.earnings:
                result[key] = value;
                break;
            default:
                // @ts-ignore
                result[key] = String(value);
        }
    }

    return result;
};

export const checkFilterInvalidValues = (filter: ISavedFilter['filter_values'], context: ISidebarFilterOptionsContext) => {
    const invalidFields: string[] = [];

    for (const [key, value] of Object.entries(filter)) {
        if (isNil(value) || isEmpty(value)) continue;

        switch (key) {
            case CmriOverviewFilterKeys.source:
                if (Array.isArray(value)) {
                    const validOptionValues = context.marketAwardSourceOptions.map(opt => opt.value);
                    if (value.some(id => !validOptionValues.includes(id))) {
                        invalidFields.push(key);
                    }
                }
                break;
            case CmriOverviewFilterKeys.status:
                if (Array.isArray(value)) {
                    const validOptionValues = context.marketAwardStatusOptions.map(opt => opt.value);
                    if (value.some(id => !validOptionValues.includes(id))) {
                        invalidFields.push(key);
                    }
                }
                break;
        }
    }

    return invalidFields;
};

export function countNotEmptyFilters(filters: Record<string, any>): number {
    if (!filters) return 0;

    return Object.values(filters).filter(value => {
        if (Array.isArray(value)) return value.length > 0;

        if (typeof value === 'number') return !isNaN(value);

        if (typeof value === 'string') return value.trim() !== '';

        if (typeof value === 'boolean') return true;

        return false;
    }).length;
}

function getEmptyFilterProperties(obj: Record<TCmriOverviewFilterKey, string>): TCmriOverviewFilterKey[] {
    return Object.entries(obj)
        .filter(([_, value]) => isEmpty(value))
        .map(([key, _]) => key) as TCmriOverviewFilterKey[];
}
